import { destroy, get, patch, post, put } from "./apiService";
import { ApiUrls } from "./ApiUrls";

// axios rejects with `error.response` set only when the server actually
// answered. A network error, timeout, or cancelled request has no
// `response`, so the catch blocks below used to hand callers a bare
// `undefined` — which then blew up downstream code that destructures
// `{ data, status }` off the result. Synthesize a minimal error shape in
// that case so every caller always gets a real object with a status.
const synthesizeNetworkError = (error: any) => ({
  status: error?.response?.status ?? 0,
  data: { detail: error?.message ?? "Network error" },
});

export type DimeAIChatTurn = {
  role: "user" | "assistant";
  content: string;
};

//: How long the chat call waits. Every other request keeps the 30s default;
//: only this one has a model and several database reads behind it.
const CHAT_TIMEOUT_MS = 120000;

//: How long attaching files waits. Scanned pages are read by OCR during the
//: request, so several scans take far longer than the 30s default.
const UPLOAD_TIMEOUT_MS = 240000;

export const sendDimeAiMessage = async (
  message: string,
  history: DimeAIChatTurn[],
  threadId?: string | null,
  companyId?: string | null,
  requestId?: string | null,
  signal?: AbortSignal,
) => {
  try {
    // The reply carries a thread_id; sending it back continues that stored
    // conversation, so the backend has the record rather than trusting
    // whatever this client happens to have on screen. `history` remains the
    // fallback for the first turn, when there is no thread yet.
    // company_id is a query parameter, not part of the body — the backend
    // resolves it against the signed-in user's own memberships either way, so
    // this narrows the choice rather than granting access.
    const url = companyId
      ? `${ApiUrls.dimeai.chat}?company_id=${encodeURIComponent(companyId)}`
      : ApiUrls.dimeai.chat;
    const res = await post(
      url,
      {
        message,
        history,
        ...(threadId ? { thread_id: threadId } : {}),
        // Our own handle on this answer, so the Stop button has something to
        // name. The server does not hand one back: the answer is the reply,
        // so by then there would be nothing left to stop.
        ...(requestId ? { request_id: requestId } : {}),
      },
      // Longer than the 30s every other call gets. A question that needs
      // several ledger lookups genuinely takes that long - measured over
      // three 55-question evaluation runs, one answer in fifty ran past 30s,
      // and the slowest took 33.5s. On those the client gave up on an answer
      // the server went on to finish and store, which is what a frozen
      // screen and then "couldn't respond" looked like to QA.
      { timeout: CHAT_TIMEOUT_MS, signal },
    );
    return res;
  } catch (error: any) {
    // A timeout has no response to read. It is worth telling apart from a
    // real failure: the server does not stop working when this client stops
    // waiting, so the answer is most likely on its way into the thread, and
    // "please try again" would have them re-ask a question already running.
    const timedOut =
      !error?.response &&
      (error?.code === "ECONNABORTED" ||
        error?.code === "ETIMEDOUT" ||
        /timeout/i.test(error?.message || ""));
    // The connection can also be cut between here and the server without a
    // timeout ever firing: a proxy or load balancer that gives up on a slow
    // answer returns 502/503/504 (an HTML page, not our JSON), and a dropped
    // connection gives no response at all. In both the server carries on and
    // stores the answer, so the user saw the question and the activity text
    // vanish into an empty page, and a refresh showed the answer. A real
    // application error always has our JSON body, so those still surface.
    const status = error?.response?.status;
    const cutOff =
      (!error?.response &&
        error?.code !== "ERR_CANCELED" &&
        !/cancel|abort/i.test(error?.message || "")) ||
      status === 502 ||
      status === 503 ||
      status === 504;
    if (timedOut || cutOff) {
      // Not a failure. The page keeps the question on screen and watches
      // the thread for the reply instead of giving the question back.
      return {
        status: 504,
        data: {
          success: false,
          stillWorking: true,
          message: "This one is taking longer than usual. It's still being answered.",
        },
      };
    }
    return error.response ?? synthesizeNetworkError(error);
  }
};

// Sample questions that name periods the company actually holds. Callers fall
// back to their own static list when this fails or comes back empty.
export const getDimeAiSuggestedQuestions = async (companyId: string) => {
  try {
    return await get(
      `${ApiUrls.dimeai.suggestedQuestions}?company_id=${encodeURIComponent(companyId)}`,
    );
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

// System Admin only. Real token usage sourced from Gemini's usageMetadata
// on every DimeAI chat call — total/today calls and tokens, plus a daily
// prompt/completion breakdown for the last `days` days.
export const getDimeAiUsageStats = async (days = 14) => {
  try {
    const res = await get(ApiUrls.dimeai.usageStats, { params: { days } });
    return res;
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

// ------------------------------------------------------------- guardrails
//
// System Admin only. These decide what question types DimeAI answers or
// declines, and a change applies to the very next question — the rules are
// read per request rather than loaded at start-up.

export type Guardrail = {
  id: string;
  name: string;
  action: "block" | "allow";
  phrases: string[];
  message: string | null;
  priority: number;
  enabled: boolean;
  modified_at?: string;
};

export type GuardrailDraft = Omit<Guardrail, "id" | "modified_at">;

export const getGuardrails = async () => {
  try {
    return await get(ApiUrls.dimeai.guardrails);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const createGuardrail = async (draft: GuardrailDraft) => {
  try {
    return await post(ApiUrls.dimeai.guardrails, draft);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const updateGuardrail = async (id: string, draft: GuardrailDraft) => {
  try {
    return await put(`${ApiUrls.dimeai.guardrails}${id}/`, draft);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const deleteGuardrail = async (id: string) => {
  try {
    return await destroy(`${ApiUrls.dimeai.guardrails}${id}/`);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

// Runs a question against the saved rules without asking it. Editing a rule
// blind and hearing from a user that it blocks too much is the failure this
// exists to prevent.
export const testGuardrails = async (question: string) => {
  try {
    return await post(ApiUrls.dimeai.guardrailTest, { question });
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

// ------------------------------------------------- terminology synonyms
//
// System Admin only (DimeAI Reference Data, table 1 of 5). Each rule widens
// what DimeAI treats as the same accounting concept -- e.g. canonical
// "Accounts Receivable" with synonyms ["AR", "Debtors", "Sundry Debtors",
// "Trade Receivables"]. Read on every question via TerminologyService.note()
// (advisor.py), so a saved change applies to the very next answer.

export type TerminologySynonym = {
  id: string;
  canonical_term: string;
  synonyms: string[];
  notes: string | null;
  enabled: boolean;
  modified_at?: string;
};

export type TerminologySynonymDraft = Omit<
  TerminologySynonym,
  "id" | "modified_at"
>;

export const getTerminologySynonyms = async () => {
  try {
    return await get(ApiUrls.dimeai.terminologySynonyms);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const createTerminologySynonym = async (
  draft: TerminologySynonymDraft,
) => {
  try {
    return await post(ApiUrls.dimeai.terminologySynonyms, draft);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const updateTerminologySynonym = async (
  id: string,
  draft: TerminologySynonymDraft,
) => {
  try {
    return await put(`${ApiUrls.dimeai.terminologySynonyms}${id}/`, draft);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const deleteTerminologySynonym = async (id: string) => {
  try {
    return await destroy(`${ApiUrls.dimeai.terminologySynonyms}${id}/`);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

// ---------------------------------------------------------- anti-synonyms
//
// System Admin only (DimeAI Reference Data, table 2 of 5). The opposite of
// terminology synonyms -- each pair is two terms that must NOT be treated as
// the same thing despite sounding alike, e.g. "Sales" vs "Sales Return".
// Read on every question via AntiSynonymService.note() (advisor.py), so a
// saved change applies to the very next answer.

export type AntiSynonym = {
  id: string;
  term_a: string;
  term_b: string;
  notes: string | null;
  enabled: boolean;
  modified_at?: string;
};

export type AntiSynonymDraft = Omit<AntiSynonym, "id" | "modified_at">;

export const getAntiSynonyms = async () => {
  try {
    return await get(ApiUrls.dimeai.antiSynonyms);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const createAntiSynonym = async (draft: AntiSynonymDraft) => {
  try {
    return await post(ApiUrls.dimeai.antiSynonyms, draft);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const updateAntiSynonym = async (
  id: string,
  draft: AntiSynonymDraft,
) => {
  try {
    return await put(`${ApiUrls.dimeai.antiSynonyms}${id}/`, draft);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const deleteAntiSynonym = async (id: string) => {
  try {
    return await destroy(`${ApiUrls.dimeai.antiSynonyms}${id}/`);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

// ----------------------------------------------------------- abbreviations
//
// System Admin only (DimeAI Reference Data, table 3 of 5). Each entry
// expands an acronym DimeAI might see in a question, e.g. "GST" -> "Goods
// and Services Tax". erp_scope pins an entry to a specific ERP (e.g.
// "Tally") when one is given; left blank it's treated as universal.

export type Abbreviation = {
  id: string;
  acronym: string;
  full_form: string;
  erp_scope: string | null;
  notes: string | null;
  enabled: boolean;
  modified_at?: string;
};

export type AbbreviationDraft = Omit<Abbreviation, "id" | "modified_at">;

export const getAbbreviations = async () => {
  try {
    return await get(ApiUrls.dimeai.abbreviations);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const createAbbreviation = async (draft: AbbreviationDraft) => {
  try {
    return await post(ApiUrls.dimeai.abbreviations, draft);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const updateAbbreviation = async (
  id: string,
  draft: AbbreviationDraft,
) => {
  try {
    return await put(`${ApiUrls.dimeai.abbreviations}${id}/`, draft);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const deleteAbbreviation = async (id: string) => {
  try {
    return await destroy(`${ApiUrls.dimeai.abbreviations}${id}/`);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

// ----------------------------------------------------------- voucher types
//
// System Admin only (DimeAI Reference Data, table 4 of 5). Each entry names
// a voucher/transaction type and explains what it means, e.g. "Contra
// Voucher: cash/bank to cash/bank transfers only". erp_scope works the same
// as on abbreviations -- voucher types genuinely differ between Tally, SAP
// and Oracle.

export type VoucherType = {
  id: string;
  name: string;
  description: string;
  erp_scope: string | null;
  enabled: boolean;
  modified_at?: string;
};

export type VoucherTypeDraft = Omit<VoucherType, "id" | "modified_at">;

export const getVoucherTypes = async () => {
  try {
    return await get(ApiUrls.dimeai.voucherTypes);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const createVoucherType = async (draft: VoucherTypeDraft) => {
  try {
    return await post(ApiUrls.dimeai.voucherTypes, draft);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const updateVoucherType = async (
  id: string,
  draft: VoucherTypeDraft,
) => {
  try {
    return await put(`${ApiUrls.dimeai.voucherTypes}${id}/`, draft);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const deleteVoucherType = async (id: string) => {
  try {
    return await destroy(`${ApiUrls.dimeai.voucherTypes}${id}/`);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

// ---------------------------------------------------------- ratio formulas
//
// System Admin only (DimeAI Reference Data, table 5 of 5). Each entry
// defines a standard ratio by name and formula text, e.g. "Current Ratio =
// Current Assets / Current Liabilities". Definition only -- DimeAI still
// computes the actual figures from the GL through its existing tools; this
// just keeps the naming and formula consistent.

export type RatioFormula = {
  id: string;
  name: string;
  formula: string;
  description: string | null;
  enabled: boolean;
  modified_at?: string;
};

export type RatioFormulaDraft = Omit<RatioFormula, "id" | "modified_at">;

export const getRatioFormulas = async () => {
  try {
    return await get(ApiUrls.dimeai.ratioFormulas);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const createRatioFormula = async (draft: RatioFormulaDraft) => {
  try {
    return await post(ApiUrls.dimeai.ratioFormulas, draft);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const updateRatioFormula = async (
  id: string,
  draft: RatioFormulaDraft,
) => {
  try {
    return await put(`${ApiUrls.dimeai.ratioFormulas}${id}/`, draft);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const deleteRatioFormula = async (id: string) => {
  try {
    return await destroy(`${ApiUrls.dimeai.ratioFormulas}${id}/`);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

// ------------------------------------------------------- conversation history
//
// US 1.6. Threads belong to the signed-in user and are filtered server-side —
// there is no way to read another person's conversations.

export type DimeAIThread = {
  id: string;
  title: string | null;
  // The organisation the conversation was held about, so reopening it can put
  // the picker back where it was rather than leaving whatever the browser
  // last remembered.
  company_id: string | null;
  message_count: number;
  last_message_at: string | null;
  created_at: string;
};

export const getDimeAiThreads = async (limit = 50) => {
  try {
    return await get(ApiUrls.dimeai.threads, { params: { limit } });
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const getDimeAiThread = async (threadId: string) => {
  try {
    return await get(`${ApiUrls.dimeai.threads}${threadId}/`);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

// Admin-only. Real advisor consumption — by week, by user and by model
// tier. Reporting only; nothing here limits anyone (US 1.7).
// start_date/end_date (YYYY-MM-DD) are optional and win over days/weeks on
// the backend when given -- see TokenUsageService._window. Either one alone
// is enough; the preset 7/30/90-day buttons still work by simply omitting
// both.
export const getDimeAiTokenUsage = async (
  days = 30,
  weeks = 12,
  startDate?: string | null,
  endDate?: string | null,
) => {
  try {
    return await get(ApiUrls.dimeai.tokenUsage, {
      params: {
        days,
        weeks,
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
      },
    });
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

// Customer-facing DimeCount: tokens (input + output), questions and a daily
// series for the last 7, 30 or 90 days. Scope is decided by the backend from
// the role (own usage, or company-wide for a Global Admin).
export const getDimeAiUsageSummary = async (days: 7 | 30 | 90 = 30) => {
  try {
    return await get(ApiUrls.dimeai.usageSummary, { params: { days } });
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

// System Admin only. Tier split of answered questions, prompt-cache hit rate
// and the companies costing the most, for the dashboard telemetry card.
export const getDimeAiRoutingTelemetry = async (days: 7 | 30 | 90 = 30) => {
  try {
    return await get(ApiUrls.dimeai.usageTelemetry, { params: { days } });
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

// ------------------------------------------------- attached documents

export type ChatDocument = {
  id: string;
  filename: string;
  kind: "spreadsheet" | "document" | "image";
  extension: string;
  size_bytes: number;
  available: boolean;
  summary: Record<string, any>;
  uploaded_at: string | null;
};

/**
 * Attach a file to a conversation (US 2.1).
 *
 * The document is read on the server — a spreadsheet into rows, a statement
 * into text — so what comes back describes what the assistant can now answer
 * about, not just that the bytes arrived.
 */
/**
 * Attach one or more files in a single request.
 *
 * One request rather than one per file, on purpose: sending each file
 * separately is what let a later file's own upload overwrite an earlier
 * one on screen — a background refresh triggered by one request could
 * land after another had already updated the attachment list, wiping out
 * its chip even though the file was safely stored. A single batched
 * request means a single response and a single state update, so there is
 * nothing for a second request to race against.
 *
 * The field name must be exactly "files", repeated once per file — that
 * is the multipart shape FastAPI's `files: List[UploadFile]` expects.
 */
export const uploadDimeAiDocuments = async (
  threadId: string | null,
  files: File[],
  companyId?: string | null,
) => {
  const data = new FormData();
  files.forEach((file) => data.append("files", file));
  // Attaching before the first question is the natural order — pick the
  // statement, then type what to ask about it. With no thread yet the server
  // creates one and returns its id, rather than the UI having to post a blank
  // message to bring a thread into being.
  const url = threadId
    ? ApiUrls.dimeai.threadDocuments(threadId)
    : `${ApiUrls.dimeai.newThreadDocument}${
        companyId ? `?company_id=${companyId}` : ""
      }`;
  try {
    return await post(url, data, {
      headers: { "Content-Type": "multipart/form-data" },
      // Reading a scan happens inside this request: each page goes to OCR.
      // The 30s default cut off a selection of three two-page scans on every
      // attempt, so this gets the same generous wait the chat call has.
      timeout: UPLOAD_TIMEOUT_MS,
    });
  } catch (error: any) {
    // The server keeps reading after this client stops waiting, so a timeout
    // is "still working", not a failure - and never the raw axios string.
    const timedOut =
      !error?.response &&
      (error?.code === "ECONNABORTED" ||
        error?.code === "ETIMEDOUT" ||
        /timeout/i.test(error?.message || ""));
    if (timedOut) {
      return {
        status: 504,
        data: {
          success: false,
          stillWorking: true,
          message:
            "Your files are taking longer than usual to read (scans can take a minute). Open History in a moment to find the conversation, or attach them again.",
        },
      };
    }
    return error.response ?? synthesizeNetworkError(error);
  }
};

/**
 * Stop an answer that is still being written.
 *
 * Aborting the request on this side only stops the waiting: the server keeps
 * working, stores the answer and spends the tokens either way. So the stop is
 * told to the server first, and the request aborted second.
 */
export const stopDimeAiMessage = async (requestId: string) => {
  try {
    return await post(ApiUrls.dimeai.stopChat, { request_id: requestId });
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const getDimeAiDocuments = async (threadId: string) => {
  try {
    return await get(ApiUrls.dimeai.threadDocuments(threadId));
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const discardDimeAiDocument = async (
  threadId: string,
  documentId: string,
) => {
  try {
    return await destroy(
      `${ApiUrls.dimeai.threadDocuments(threadId)}${documentId}/`,
    );
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

/**
 * Vote on one of DimeAI's own answers. Captured silently — no prompt for a
 * reason, no confirmation toast beyond the button's own state changing.
 * Voting again on the same message overwrites the earlier vote.
 */
export const sendDimeAiFeedback = async (
  messageId: string,
  rating: "up" | "down",
) => {
  try {
    return await post(ApiUrls.dimeai.feedback, {
      message_id: messageId,
      rating,
    });
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

// ------------------------------------------------- tag-independent periods
//
// System Admin only (DimeAI Tag-Independent Mode). Each range marks a
// company + period window where DimeAI's certification-based tag
// withholding does not apply -- the row's own tag travels regardless of
// whether its GL Group was certified for that period. Backend router is
// standalone ("/tag-independent-periods", not nested under "ai-advisor/"),
// see ApiUrls.dimeai.tagIndependentPeriods.

export type TagIndependentPeriod = {
  id: string;
  company: string;
  start_period: string;
  end_period: string | null;
  is_enabled: boolean;
  created_by?: string | null;
  updated_by?: string | null;
  created_at: string;
};

export type TagIndependentPeriodDraft = {
  company: string;
  start_period: string;
  end_period?: string | null;
};

/** Every company's ranges at once -- the admin homepage view. */
export const getAllTagIndependentPeriods = async () => {
  try {
    return await get(ApiUrls.dimeai.tagIndependentPeriods);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const getTagIndependentPeriods = async (companyId: string) => {
  try {
    return await get(`${ApiUrls.dimeai.tagIndependentPeriods}${companyId}/`);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const createTagIndependentPeriod = async (
  draft: TagIndependentPeriodDraft,
) => {
  try {
    return await post(ApiUrls.dimeai.tagIndependentPeriods, draft);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const updateTagIndependentPeriodEnabled = async (
  id: string,
  isEnabled: boolean,
) => {
  try {
    return await patch(`${ApiUrls.dimeai.tagIndependentPeriods}${id}/`, {
      is_enabled: isEnabled,
    });
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

export const deleteTagIndependentPeriod = async (id: string) => {
  try {
    return await destroy(`${ApiUrls.dimeai.tagIndependentPeriods}${id}/`);
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};

// System Admin only. Every answer a user has thumbs-downed, across every
// company, for the DimeAI Feedback review tab.
export const getDimeAiThumbsDownFeedback = async (
  limit = 100,
  offset = 0,
) => {
  try {
    return await get(ApiUrls.dimeai.thumbsDownFeedback, {
      params: { limit, offset },
    });
  } catch (error: any) {
    return error.response ?? synthesizeNetworkError(error);
  }
};
